import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ComposedChart, AreaChart, Area, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import './SAService.css';

const BASE = 'https://fotileams.com/api';
const PAGE_SIZE = 25;

// Boss portal uses its own token, not the employee one.
function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('super_token')}`,
  };
}

const ZONE_COLOR = {
  karachi: '#10b981', lahore: '#3b82f6', islamabad: '#8b5cf6', multan: '#f97316',
};
const zoneColor = (z) => ZONE_COLOR[String(z || '').toLowerCase()] || '#94a3b8';
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '—');

function formatPKR(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return `PKR ${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000)    return `PKR ${Math.round(v / 1000)}K`;
  return `PKR ${v.toLocaleString('en-PK')}`;
}

function shortDate(d) {
  if (!d) return '';
  const dt = new Date(`${String(d).substring(0, 10)}T00:00:00`);
  return dt.toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
}

function hours(h) {
  const v = Number(h) || 0;
  if (v >= 24) return `${(v / 24).toFixed(1)}d`;
  return `${Math.round(v)}h`;
}

// Age readable at a glance — minutes, hours, then days.
function ageLabel(h) {
  const v = Number(h) || 0;
  if (v < 1)  return `${Math.round(v * 60)}m`;
  if (v < 24) return `${v.toFixed(1)}h`;
  return `${(v / 24).toFixed(1)}d`;
}

// The column the eye should be drawn to.
function ageColor(h) {
  const v = Number(h) || 0;
  if (v >= 120) return '#dc2626';
  if (v >= 72)  return '#f59e0b';
  if (v >= 48)  return '#d97706';
  return '#64748b';
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-PK', {
    timeZone: 'Asia/Karachi', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function getInitials(name) {
  if (!name) return '?';
  const p = String(name).trim().split(' ');
  return (p[0][0] + (p[p.length - 1]?.[0] || '')).toUpperCase();
}

const ChartTip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="sv-tip">
      <div className="sv-tip-label">{typeof label === 'number' ? `${label}${suffix}` : shortDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} className="sv-tip-row">
          <span className="sv-tip-dot" style={{ background: p.color || p.fill }} />
          <span className="sv-tip-name">{p.name}</span>
          <span className="sv-tip-val">
            {p.name === 'Revenue' ? formatPKR(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function SAService() {
  // Summary feed
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [techSort, setTechSort] = useState('completed');
  const [showForecast, setShowForecast] = useState(false);

  // Recent complaints — loaded separately so a slow or failing complaints
  // fetch can never stop the main dashboard from rendering.
  const [days, setDays]         = useState(2);
  const [recent, setRecent]     = useState(null);
  const [recentLoading, setRL]  = useState(true);
  const [recentError, setRE]    = useState(null);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');
  const [zoneFilter, setZone]   = useState('all');
  const [page, setPage]         = useState(1);
  const [sortBy, setSortBy]     = useState('age');

  // Karachi technician scorecard — cross-system join (AMS attendance +
  // service performance). Loaded independently, same pattern as
  // fetchRecent, so a slow/failing scorecard fetch never blocks the rest
  // of the dashboard.
  const [tsc, setTsc]         = useState(null);
  const [tscLoading, setTscL] = useState(true);
  const [tscError, setTscE]   = useState(null);

  const fetchData = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true); else setLoad(true);
      setError(null);
      const res  = await fetch(`${BASE}/service/feed${force ? '?refresh=1' : ''}`,
        { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) { setError(json.error || 'Failed to load service data'); setData(null); }
      else setData(json);
    } catch {
      setError('Network error — could not reach the server');
      setData(null);
    } finally { setLoad(false); setRefreshing(false); }
  }, []);

  const fetchRecent = useCallback(async (d, force = false) => {
    try {
      setRL(true); setRE(null);
      const res  = await fetch(`${BASE}/service/recent?days=${d}${force ? '&refresh=1' : ''}`,
        { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) { setRE(json.error || 'Failed to load recent complaints'); setRecent(null); }
      else { setRecent(json); setPage(1); }
    } catch {
      setRE('Network error — could not reach the server');
      setRecent(null);
    } finally { setRL(false); }
  }, []);

  const fetchScorecard = useCallback(async (force = false) => {
    try {
      setTscL(true); setTscE(null);
      const res  = await fetch(`${BASE}/service/technicians${force ? '?refresh=1' : ''}`,
        { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) { setTscE(json.error || 'Failed to load scorecard'); setTsc(null); }
      else setTsc(json);
    } catch {
      setTscE('Network error — could not reach the server');
      setTsc(null);
    } finally { setTscL(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchRecent(days); }, [days, fetchRecent]);
  useEffect(() => { fetchScorecard(); }, [fetchScorecard]);

  const refreshAll = () => { fetchData(true); fetchRecent(days, true); fetchScorecard(true); };

  // ── Recent complaints derived state (hooks must run before any return) ──
  const rows = recent?.recent_complaints || [];
  const ra   = recent?.analysis || {};
  const rcounts = recent?.recent_meta?.counts || {};

  const visible = useMemo(() => {
    let out = [...rows];
    if (filter === 'unassigned') out = out.filter(r => r.unassigned);
    if (filter === 'ageing')     out = out.filter(r => r.is_ageing);
    if (filter === 'urgent')     out = out.filter(r => r.is_urgent);
    if (filter === 'completed')  out = out.filter(r => String(r.status).toLowerCase() === 'completed');
    if (zoneFilter !== 'all')    out = out.filter(r => r.zone === zoneFilter);

    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        (r.complaint_no || '').toLowerCase().includes(q) ||
        (r.customer     || '').toLowerCase().includes(q) ||
        (r.product      || '').toLowerCase().includes(q) ||
        (r.issue        || '').toLowerCase().includes(q) ||
        (r.location     || '').toLowerCase().includes(q) ||
        (r.technician   || '').toLowerCase().includes(q)
      );
    }

    if (sortBy === 'age')      out.sort((x, y) => (y.age_hours || 0) - (x.age_hours || 0));
    if (sortBy === 'newest')   out.sort((x, y) => (x.age_hours || 0) - (y.age_hours || 0));
    if (sortBy === 'customer') out.sort((x, y) => (x.customer || '').localeCompare(y.customer || ''));
    return out;
  }, [rows, filter, zoneFilter, search, sortBy]);

  if (loading) {
    return (
      <div className="sv-root">
        <div className="sv-loading">
          <div className="sv-spinner" />
          <p>Connecting to the service portal…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="sv-root">
        <div className="sv-error">
          <div className="sv-error-icon">🔌</div>
          <div className="sv-error-title">Service portal unavailable</div>
          <div className="sv-error-sub">{error}</div>
          <button className="sv-retry" onClick={refreshAll}>Try again</button>
        </div>
      </div>
    );
  }

  const h        = data.headline || {};
  const risk     = data.risk || {};
  const zones    = data.zones || [];
  const techs    = data.technicians || [];
  const stock    = data.stock || {};
  const sat      = data.satisfaction || {};
  const series   = data.daily_series || [];
  const products = data.top_failing_products || [];
  const malf     = data.top_malfunctions || [];
  const centre   = data.service_centre || {};
  const alerts   = data.insights?.alerts || [];
  const wins     = data.insights?.wins || [];
  const fq       = data.forecast_quality || {};
  const meta     = data._meta || {};

  const chartData = series.map(d => ({
    date: d.date, New: d.new, Completed: d.completed, Revenue: d.revenue,
  }));

  const forecastData = (data.forecast?.points || []).map(p => ({
    date: p.date, Projected: p.value,
  }));

  const turnaroundChange = (h.avg_turnaround_hours && h.prev_avg_turnaround_hours)
    ? Math.round(((h.avg_turnaround_hours - h.prev_avg_turnaround_hours) / h.prev_avg_turnaround_hours) * 100)
    : null;

  const sortedTechs = [...techs].sort((a, b) => {
    if (techSort === 'completed') return (b.completed_30d || 0) - (a.completed_30d || 0);
    if (techSort === 'assigned')  return (b.assigned_30d || 0) - (a.assigned_30d || 0);
    if (techSort === 'revenue')   return (b.revenue_30d || 0) - (a.revenue_30d || 0);
    if (techSort === 'turnaround') {
      const av = a.avg_turnaround_hours || Infinity, bv = b.avg_turnaround_hours || Infinity;
      return av - bv;
    }
    return 0;
  });

  const activeTechs = sortedTechs.filter(t => (t.assigned_30d || 0) > 0 || (t.completed_30d || 0) > 0);
  const idleTechs   = sortedTechs.filter(t => (t.assigned_30d || 0) === 0 && (t.completed_30d || 0) === 0);

  const maxProduct = Math.max(1, ...products.map(p => p.complaints_90d || 0));
  const maxMalf    = Math.max(1, ...malf.map(m => m.count_90d || 0));

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows   = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const zoneOptions = (ra.zones || []).map(z => z.zone);
  const maxHour = Math.max(1, ...(ra.hourly || []).map(x => x.count));

  return (
    <div className="sv-root">

      {/* ── HEADER ── */}
      <div className="sv-header">
        <div>
          <h1 className="sv-title">Service Operations</h1>
          <p className="sv-sub">
            Live from the Fotile Service Portal · {cap(String(data.scope || '').replace('all_', ''))}
            {meta.cached && ` · cached ${Math.round((meta.age_seconds || 0) / 60)}m ago`}
          </p>
        </div>
        <button className="sv-refresh" onClick={refreshAll} disabled={refreshing}>
          {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {meta.stale && <div className="sv-stale">⚠️ {meta.warning}</div>}

      {/* ── NEEDS ATTENTION ── */}
      {alerts.length > 0 && (
        <div className="sv-alerts">
          <div className="sv-alerts-head">Needs attention</div>
          {alerts.map((a, i) => (
            <div key={i} className={`sv-alert sv-alert-${a.severity}`}>
              <span className="sv-alert-sev">{a.severity === 'high' ? '🔴' : '🟠'}</span>
              <div>
                <div className="sv-alert-title">{a.title}</div>
                <div className="sv-alert-detail">{a.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {wins.length > 0 && (
        <div className="sv-wins">
          {wins.map((w, i) => (
            <div key={i} className="sv-win">
              <span className="sv-win-ico">✓</span>
              <div>
                <div className="sv-win-title">{w.title}</div>
                <div className="sv-win-detail">{w.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── KPI BAND ── */}
      <div className="sv-kpis">
        <div className="sv-kpi sv-kpi-warn">
          <div className="sv-kpi-lbl">Open Complaints</div>
          <div className="sv-kpi-num">{(h.open || 0).toLocaleString()}</div>
          <div className="sv-kpi-foot">{(h.total || 0).toLocaleString()} total · {h.in_progress || 0} in progress</div>
        </div>
        <div className="sv-kpi">
          <div className="sv-kpi-lbl">Closure Rate</div>
          <div className="sv-kpi-num">{h.closure_rate_pct ?? 0}<span>%</span></div>
          <div className="sv-kpi-foot">{h.completed || 0} completed</div>
        </div>
        <div className="sv-kpi sv-kpi-hero">
          <div className="sv-kpi-lbl">Avg Turnaround</div>
          <div className="sv-kpi-num">{hours(h.avg_turnaround_hours)}</div>
          <div className="sv-kpi-foot">
            {turnaroundChange !== null && (
              <span className={`sv-delta ${turnaroundChange < 0 ? 'good' : 'bad'}`}>
                {turnaroundChange < 0 ? '↓' : '↑'} {Math.abs(turnaroundChange)}%
              </span>
            )}
            <span className="sv-kpi-vs">was {hours(h.prev_avg_turnaround_hours)}</span>
          </div>
        </div>
        <div className="sv-kpi">
          <div className="sv-kpi-lbl">Revenue This Month</div>
          <div className="sv-kpi-num sv-kpi-num-sm">{formatPKR(h.mtd_revenue)}</div>
          <div className="sv-kpi-foot">last month {formatPKR(h.last_month_revenue)}</div>
        </div>
        <div className="sv-kpi">
          <div className="sv-kpi-lbl">Unassigned</div>
          <div className="sv-kpi-num sv-kpi-danger">{(risk.unassigned_open || 0).toLocaleString()}</div>
          <div className="sv-kpi-foot">no technician allocated</div>
        </div>
      </div>

      {/* ── DAILY VOLUME ── */}
      <div className="sv-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-card-title">Daily Volume &amp; Revenue</div>
            <div className="sv-card-sub">
              New complaints against completions. When the blue bars run ahead of
              the green ones day after day, the backlog is growing.
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="svNew" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.7} />
              </linearGradient>
              <linearGradient id="svDone" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} interval={6}
              tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="l" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false} tickLine={false}
              tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
            <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(59,130,246,0.05)' }} />
            <Bar yAxisId="l" dataKey="New" fill="url(#svNew)" radius={[4, 4, 0, 0]} maxBarSize={14} />
            <Bar yAxisId="l" dataKey="Completed" fill="url(#svDone)" radius={[4, 4, 0, 0]} maxBarSize={14} />
            <Line yAxisId="r" type="monotone" dataKey="Revenue" stroke="#f59e0b" strokeWidth={2}
              dot={false} activeDot={{ r: 4 }} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="sv-legend">
          <span><i style={{ background: '#3b82f6' }} />New complaints</span>
          <span><i style={{ background: '#10b981' }} />Completed</span>
          <span><i className="sv-legend-line" />Revenue</span>
        </div>
      </div>

      {/* ── ZONES ── */}
      <div className="sv-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-card-title">Zone Performance — last 90 days</div>
            <div className="sv-card-sub">
              Turnaround is the number that matters here. A zone closing jobs three
              times slower than another has a process problem, not a volume problem.
            </div>
          </div>
        </div>
        <div className="sv-zones">
          {zones.map(z => {
            const col = zoneColor(z.zone);
            const tCol = z.avg_turnaround_hours >= 120 ? '#ef4444'
              : z.avg_turnaround_hours >= 72 ? '#f59e0b' : '#10b981';
            return (
              <div key={z.zone} className="sv-zone">
                <div className="sv-zone-head">
                  <span className="sv-zone-dot" style={{ background: col }} />
                  <span className="sv-zone-name">{cap(z.zone)}</span>
                  <span className="sv-zone-vol">{z.total_90d} jobs</span>
                </div>
                <div className="sv-zone-metrics">
                  <div className="sv-zone-metric">
                    <span className="sv-zm-val" style={{ color: tCol }}>{hours(z.avg_turnaround_hours)}</span>
                    <span className="sv-zm-lbl">turnaround</span>
                  </div>
                  <div className="sv-zone-metric">
                    <span className="sv-zm-val">{z.closure_rate_pct}%</span>
                    <span className="sv-zm-lbl">closed</span>
                  </div>
                  <div className="sv-zone-metric">
                    <span className="sv-zm-val sv-zm-open">{z.open}</span>
                    <span className="sv-zm-lbl">still open</span>
                  </div>
                  <div className="sv-zone-metric">
                    <span className="sv-zm-val sv-zm-rev">{formatPKR(z.revenue_90d)}</span>
                    <span className="sv-zm-lbl">revenue</span>
                  </div>
                </div>
                <div className="sv-zone-track">
                  <div className="sv-zone-fill"
                    style={{ width: `${Math.min(100, z.closure_rate_pct)}%`, background: col }} />
                </div>
                <div className="sv-zone-foot">
                  {z.completed} completed · {z.cancelled || 0} cancelled
                  {z.avg_rating ? ` · ${z.avg_rating}/5 rated` : ' · not yet rated'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── TECHNICIANS (30-day performance) ── */}
      <div className="sv-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-card-title">Technicians — last 30 days</div>
            <div className="sv-card-sub">
              Anyone holding assigned jobs with no completions is a blockage, not
              a performance statistic.
            </div>
          </div>
          <div className="sv-sorts">
            {[
              { k: 'completed',  l: 'Completed' },
              { k: 'assigned',   l: 'Assigned' },
              { k: 'turnaround', l: 'Fastest' },
              { k: 'revenue',    l: 'Revenue' },
            ].map(s => (
              <button key={s.k}
                className={`sv-sort ${techSort === s.k ? 'on' : ''}`}
                onClick={() => setTechSort(s.k)}>{s.l}</button>
            ))}
          </div>
        </div>

        <div className="sv-techs">
          {activeTechs.map(t => {
            const pct = t.completion_pct ?? 0;
            const col = pct >= 60 ? '#10b981' : pct >= 35 ? '#f59e0b' : '#ef4444';
            const stalled = (t.assigned_30d || 0) > 0 && (t.completed_30d || 0) === 0;
            return (
              <div key={t.username} className={`sv-tech ${stalled ? 'sv-tech-stalled' : ''}`}>
                <div className="sv-tech-av" style={{ background: zoneColor(t.zone) }}>
                  {getInitials(t.name)}
                </div>
                <div className="sv-tech-info">
                  <div className="sv-tech-name">
                    {t.name}
                    {stalled && <span className="sv-tech-flag">nothing completed</span>}
                  </div>
                  <div className="sv-tech-meta">
                    {cap(t.zone)} · {t.assigned_30d} assigned · {t.completed_30d} done
                    {t.avg_turnaround_hours > 0 && ` · ${hours(t.avg_turnaround_hours)} avg`}
                  </div>
                </div>
                <div className="sv-tech-bar">
                  <div className="sv-tech-track">
                    <div className="sv-tech-fill" style={{ width: `${pct}%`, background: col }} />
                  </div>
                  <span className="sv-tech-pct" style={{ color: col }}>{pct}%</span>
                </div>
                <div className="sv-tech-rev">{formatPKR(t.revenue_30d)}</div>
              </div>
            );
          })}

          {idleTechs.length > 0 && (
            <div className="sv-idle">
              <b>{idleTechs.length} technician{idleTechs.length > 1 ? 's' : ''} had no jobs assigned at all:</b>
              {' '}{idleTechs.map(t => `${t.name} (${cap(t.zone)})`).join(', ')}
            </div>
          )}
        </div>
      </div>

      {/* ── KARACHI TECHNICIAN SCORECARD (cross-system) ── */}
      <div className="sv-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-card-title">Karachi Technician Scorecard</div>
            <div className="sv-card-sub">
              AMS attendance joined against service-portal performance for the
              same people — one score neither system shows alone.
            </div>
          </div>
        </div>

        {tscLoading ? (
          <div className="sv-recent-loading"><div className="sv-spinner" /><p>Joining attendance and service data…</p></div>
        ) : tscError ? (
          <div className="sv-recent-error">
            ⚠️ {tscError}
            <button className="sv-retry-sm" onClick={() => fetchScorecard(true)}>Retry</button>
          </div>
        ) : (
          <>
            {tsc.linked_count < tsc.total_count && (
              <div className="sv-tsc-note">
                {tsc.total_count - tsc.linked_count} of {tsc.total_count} technicians shown here
                aren't linked to an AMS employee record yet — they show service stats only.
              </div>
            )}

            <div className="sv-tsc-grid">
              {tsc.technicians.map(t => (
                <div key={t.name} className={`sv-tsc-card sv-tsc-${t.verdict.tone}`}>
                  <div className="sv-tsc-top">
                    <div className="sv-tsc-name">
                      {t.name}
                      {t.linked && t.ams?.confidence === 'medium' && (
                        <span className="sv-tsc-unsure" title="Best-effort name match, not confirmed">?</span>
                      )}
                    </div>
                    <span className={`sv-tsc-verdict sv-tv-${t.verdict.tone}`}>{t.verdict.label}</span>
                  </div>

                  <div className="sv-tsc-body">
                    <div className="sv-tsc-half">
                      <div className="sv-tsc-half-lbl">Service (30d)</div>
                      <div className="sv-tsc-stat">
                        <b>{t.service.completed_30d}</b>/{t.service.assigned_30d} jobs done
                      </div>
                      <div className="sv-tsc-substat">
                        {t.service.completion_pct}% completion
                        {t.service.avg_turnaround_hours > 0 && ` · ${Math.round(t.service.avg_turnaround_hours)}h avg`}
                      </div>
                    </div>

                    <div className="sv-tsc-divider" />

                    <div className="sv-tsc-half">
                      <div className="sv-tsc-half-lbl">AMS (this month)</div>
                      {t.ams ? (
                        <>
                          <div className="sv-tsc-stat">
                            <b>{t.ams.hours_completion}%</b> hours completed
                          </div>
                          <div className="sv-tsc-substat">
                            {t.ams.absent_days} absent · {t.ams.late_days} late
                            {!t.ams.is_eligible && ' · building up'}
                          </div>
                        </>
                      ) : (
                        <div className="sv-tsc-nolink">
                          {t.linked ? 'No AMS record found for this ID' : 'Not linked in AMS yet'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="sv-two-col">
        {/* ── PRODUCTS ── */}
        <div className="sv-card">
          <div className="sv-card-head">
            <div>
              <div className="sv-card-title">What's Breaking</div>
              <div className="sv-card-sub">Complaints by product, 90 days · warranty share alongside</div>
            </div>
          </div>
          <div className="sv-bars">
            {products.slice(0, 8).map(p => (
              <div key={p.model} className="sv-bar-row">
                <div className="sv-bar-label">
                  {p.model}
                  <span className="sv-bar-warranty">{p.in_warranty_pct}% in warranty</span>
                </div>
                <div className="sv-bar-wrap">
                  <div className="sv-bar-track">
                    <div className="sv-bar-fill"
                      style={{ width: `${(p.complaints_90d / maxProduct) * 100}%` }} />
                  </div>
                  <span className="sv-bar-num">{p.complaints_90d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MALFUNCTIONS ── */}
        <div className="sv-card">
          <div className="sv-card-head">
            <div>
              <div className="sv-card-title">Most Common Faults</div>
              <div className="sv-card-sub">Reported malfunctions, 90 days</div>
            </div>
          </div>
          <div className="sv-bars">
            {malf.slice(0, 8).map(m => (
              <div key={m.malfunction} className="sv-bar-row">
                <div className="sv-bar-label">{cap(m.malfunction)}</div>
                <div className="sv-bar-wrap">
                  <div className="sv-bar-track">
                    <div className="sv-bar-fill sv-bar-amber"
                      style={{ width: `${(m.count_90d / maxMalf) * 100}%` }} />
                  </div>
                  <span className="sv-bar-num">{m.count_90d}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sv-two-col">
        {/* ── RISK ── */}
        <div className="sv-card">
          <div className="sv-card-head">
            <div>
              <div className="sv-card-title">Operational Risk</div>
              <div className="sv-card-sub">Where jobs are getting stuck</div>
            </div>
          </div>
          <div className="sv-risk">
            {[
              { l: 'Open more than 3 days',  v: risk.open_over_3_days,  hot: risk.open_over_3_days > 200 },
              { l: 'Open more than 7 days',  v: risk.open_over_7_days,  hot: risk.open_over_7_days > 200 },
              { l: 'No technician assigned', v: risk.unassigned_open,   hot: risk.unassigned_open > 100 },
              { l: 'Marked urgent',          v: risk.urgent_open,       hot: risk.urgent_open > 0 },
              { l: 'Sitting at service centre', v: risk.at_service_centre, hot: false },
              { l: 'Parts below reorder',    v: risk.parts_below_reorder, hot: risk.parts_below_reorder > 20 },
              { l: 'Repeat visits (90d)',    v: risk.repeat_visits_90d, hot: risk.repeat_rate_pct > 10 },
            ].map(r => (
              <div key={r.l} className="sv-risk-row">
                <span className="sv-risk-lbl">{r.l}</span>
                <span className={`sv-risk-val ${r.hot ? 'hot' : ''}`}>{(r.v ?? 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="sv-risk-foot">
            Repeat rate {risk.repeat_rate_pct ?? 0}% — jobs closed that came back
          </div>
        </div>

        {/* ── STOCK + SATISFACTION ── */}
        <div className="sv-card">
          <div className="sv-card-head">
            <div>
              <div className="sv-card-title">Parts &amp; Customers</div>
              <div className="sv-card-sub">Stock position and feedback</div>
            </div>
          </div>

          <div className="sv-stock-grid">
            <div className="sv-stock-item">
              <span className="sv-stock-num">{(stock.catalog_parts || 0).toLocaleString()}</span>
              <span className="sv-stock-lbl">parts in catalogue</span>
            </div>
            <div className="sv-stock-item">
              <span className="sv-stock-num">{(stock.units_on_shelf || 0).toLocaleString()}</span>
              <span className="sv-stock-lbl">units on shelf</span>
            </div>
            <div className="sv-stock-item">
              <span className="sv-stock-num sv-stock-hot">{stock.parts_below_reorder || 0}</span>
              <span className="sv-stock-lbl">below reorder</span>
            </div>
            <div className="sv-stock-item">
              <span className="sv-stock-num">{stock.requests_awaiting_approval || 0}</span>
              <span className="sv-stock-lbl">requests waiting</span>
            </div>
          </div>

          {(stock.low_stock_items || []).length > 0 && (
            <div className="sv-lowstock">
              <div className="sv-lowstock-head">
                Running out — {(stock.low_stock_items || []).length} shown
              </div>
              <div className="sv-lowstock-list">
                {(stock.low_stock_items || []).slice(0, 8).map(it => (
                  <div key={it.code} className="sv-lowstock-row">
                    <span className="sv-ls-name">{it.name}</span>
                    <span className="sv-ls-zone">{cap(it.zone)}</span>
                    <span className="sv-ls-qty">{it.on_shelf} left</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sv-sat">
            <div className="sv-sat-score">
              <span className="sv-sat-num">{sat.avg_rating ?? '—'}</span>
              <span className="sv-sat-of">/ 5</span>
            </div>
            <div className="sv-sat-info">
              <div className="sv-sat-count">{sat.ratings_count || 0} ratings collected</div>
              <div className="sv-sat-sub">
                {sat.response_rate_pct ?? 0}% response rate · {sat.unhappy_3_or_less || 0} rated 3 or below
              </div>
            </div>
          </div>

          {(centre.awaiting_acceptance > 0 || centre.in_workshop > 0) && (
            <div className="sv-centre">
              <b>Service centre:</b> {centre.awaiting_acceptance || 0} awaiting acceptance,
              {' '}{centre.in_workshop || 0} in workshop
            </div>
          )}
        </div>
      </div>

      {/* ── FORECAST ── */}
      {fq.usable && (
        <div className="sv-card">
          <button className="sv-fc-toggle" onClick={() => setShowForecast(v => !v)}>
            <span>📉 14-day projection</span>
            <span className={`sv-fc-grade sv-fc-${fq.grade}`}>{fq.grade}</span>
            <span className={`sv-fc-chev ${showForecast ? 'open' : ''}`}>›</span>
          </button>

          {showForecast && (
            <>
              <div className={`sv-fc-warn sv-fc-warn-${fq.grade}`}>
                <span className="sv-fc-warn-ico">{fq.grade === 'unreliable' ? '⚠️' : 'ℹ️'}</span>
                <div>
                  <div>{fq.note}</div>
                  {fq.contradiction && <div className="sv-fc-contradiction">{fq.contradiction}</div>}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={190}>
                <AreaChart data={forecastData} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="svFc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate}
                    tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip />} />
                  <Area type="monotone" dataKey="Projected" stroke="#64748b" strokeWidth={2}
                    strokeDasharray="5 4" fill="url(#svFc)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
              <div className="sv-fc-foot">
                Dashed because it's a projection, not a measurement.
                Last 7 days actual: {data.forecast?.last_7_actual ?? '—'} ·
                {' '}next 7 projected: {Math.round(data.forecast?.next_7_projected ?? 0)}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          LIVE COMPLAINTS — the actual jobs behind the numbers above.
          Loaded independently of the summary feed, so a slow or failing
          complaints fetch never blocks the dashboard from rendering.
      ═══════════════════════════════════════════════════════════ */}
      <div className="sv-divider">
        <span>Live complaints</span>
      </div>

      <div className="sv-card sv-recent-head-card">
        <div className="sv-card-head">
          <div>
            <div className="sv-card-title">Recent Complaints</div>
            <div className="sv-card-sub">
              Every job that came in over the last {days} day{days > 1 ? 's' : ''} — these are
              the individual complaints behind the totals above.
              {recent?.generated_at && ` Updated ${fmtTime(recent.generated_at)}.`}
            </div>
          </div>
          <div className="sv-window">
            {[2, 7].map(d => (
              <button key={d} className={`sv-win-btn ${days === d ? 'on' : ''}`}
                onClick={() => setDays(d)}>{d} days</button>
            ))}
          </div>
        </div>

        {recentLoading ? (
          <div className="sv-recent-loading"><div className="sv-spinner" /><p>Loading complaints…</p></div>
        ) : recentError ? (
          <div className="sv-recent-error">
            ⚠️ {recentError}
            <button className="sv-retry-sm" onClick={() => fetchRecent(days, true)}>Retry</button>
          </div>
        ) : (
          <>
            {/* Findings */}
            {(ra.findings || []).length > 0 && (
              <div className="sv-findings">
                {ra.findings.map((f, i) => (
                  <div key={i} className={`sv-finding sv-f-${f.severity}`}>
                    <span className="sv-f-ico">
                      {f.severity === 'high' ? '🔴' : f.severity === 'medium' ? '🟠' : 'ℹ️'}
                    </span>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Clickable summary chips */}
            <div className="sv-strip">
              <button className={`sv-chip ${filter === 'all' ? 'on' : ''}`}
                onClick={() => { setFilter('all'); setPage(1); }}>
                <span className="sv-chip-num">{rows.length}</span>
                <span className="sv-chip-lbl">Total</span>
              </button>
              <button className={`sv-chip sv-chip-red ${filter === 'urgent' ? 'on' : ''}`}
                onClick={() => { setFilter('urgent'); setPage(1); }}>
                <span className="sv-chip-num">{rcounts.urgent ?? 0}</span>
                <span className="sv-chip-lbl">Urgent</span>
              </button>
              <button className={`sv-chip sv-chip-amber ${filter === 'unassigned' ? 'on' : ''}`}
                onClick={() => { setFilter('unassigned'); setPage(1); }}>
                <span className="sv-chip-num">{rcounts.unassigned ?? 0}</span>
                <span className="sv-chip-lbl">Unassigned</span>
              </button>
              <button className={`sv-chip sv-chip-amber ${filter === 'ageing' ? 'on' : ''}`}
                onClick={() => { setFilter('ageing'); setPage(1); }}>
                <span className="sv-chip-num">{rcounts.ageing ?? 0}</span>
                <span className="sv-chip-lbl">Ageing</span>
              </button>
              <button className={`sv-chip sv-chip-green ${filter === 'completed' ? 'on' : ''}`}
                onClick={() => { setFilter('completed'); setPage(1); }}>
                <span className="sv-chip-num">{rcounts.completed ?? 0}</span>
                <span className="sv-chip-lbl">Completed</span>
              </button>
              <div className="sv-strip-median">
                <span className="sv-median-num">{ageLabel(ra.age?.median_hours)}</span>
                <span className="sv-median-lbl">median age</span>
              </div>
            </div>
          </>
        )}
      </div>

      {!recentLoading && !recentError && (
        <>
          {/* Charts for the live window */}
          <div className="sv-two-col">
            <div className="sv-card">
              <div className="sv-card-title">How long they've been waiting</div>
              <div className="sv-card-sub">
                Bars drifting right means work is sitting rather than moving.
              </div>
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={ra.age?.buckets || []} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                  <Bar dataKey="count" name="Complaints" radius={[5, 5, 0, 0]} maxBarSize={46}>
                    {(ra.age?.buckets || []).map((b, i) => <Cell key={i} fill={b.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="sv-card-foot">
                Median {ageLabel(ra.age?.median_hours)} · oldest {ageLabel(ra.age?.oldest_hours)}
              </div>
            </div>

            <div className="sv-card">
              <div className="sv-card-title">Who's carrying the work</div>
              <div className="sv-card-sub">
                Unassigned shown separately — that's a dispatch gap, not a workload.
              </div>
              <div className="sv-techload">
                {ra.unassigned_count > 0 && (
                  <div className="sv-tl-row sv-tl-unassigned">
                    <span className="sv-tl-name">Nobody assigned</span>
                    <div className="sv-tl-track">
                      <div className="sv-tl-fill sv-tl-fill-red"
                        style={{ width: `${(ra.unassigned_count / Math.max(1, rows.length)) * 100}%` }} />
                    </div>
                    <span className="sv-tl-num">{ra.unassigned_count}</span>
                  </div>
                )}
                {(ra.technicians || []).slice(0, 7).map(t => (
                  <div key={t.technician} className="sv-tl-row">
                    <span className="sv-tl-name">
                      {t.technician}
                      <span className="sv-tl-zone" style={{ color: zoneColor(t.zone) }}>{cap(t.zone)}</span>
                    </span>
                    <div className="sv-tl-track">
                      <div className="sv-tl-fill"
                        style={{ width: `${(t.total / Math.max(1, rows.length)) * 100}%`,
                                 background: zoneColor(t.zone) }} />
                    </div>
                    <span className="sv-tl-num">
                      {t.total}
                      {t.ageing > 0 && <span className="sv-tl-ageing">{t.ageing} ageing</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="sv-two-col">
            <div className="sv-card">
              <div className="sv-card-title">When complaints arrive</div>
              <div className="sv-card-sub">By hour of day — useful for call desk cover.</div>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={ra.hourly || []} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="hour" interval={2} tickFormatter={x => `${x}:00`}
                    tick={{ fontSize: 9.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip suffix=":00" />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                  <Bar dataKey="count" name="Arrived" radius={[3, 3, 0, 0]} maxBarSize={18}>
                    {(ra.hourly || []).map((x, i) => (
                      <Cell key={i} fill={x.count >= maxHour * 0.75 ? '#6366f1' : '#c7d2fe'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="sv-card">
              <div className="sv-card-title">This window by zone</div>
              <div className="sv-card-sub">Volume, and how much has nobody assigned.</div>
              <div className="sv-rzones">
                {(ra.zones || []).map(z => {
                  const unPct = z.total > 0 ? Math.round((z.unassigned / z.total) * 100) : 0;
                  return (
                    <div key={z.zone} className="sv-rzone">
                      <div className="sv-rzone-top">
                        <span className="sv-zone-dot" style={{ background: zoneColor(z.zone) }} />
                        <span className="sv-rzone-name">{cap(z.zone)}</span>
                        <span className="sv-rzone-total">{z.total}</span>
                      </div>
                      <div className="sv-rzone-track">
                        <div className="sv-rzone-fill"
                          style={{ width: `${(z.total / Math.max(1, rows.length)) * 100}%`,
                                   background: zoneColor(z.zone) }} />
                      </div>
                      <div className="sv-rzone-foot">
                        <span className={unPct >= 40 ? 'sv-hot' : ''}>{z.unassigned} unassigned ({unPct}%)</span>
                        <span>{z.ageing} ageing</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="sv-status-mix">
                <div className="sv-status-title">Status mix</div>
                {(ra.statuses || []).map(s => (
                  <div key={s.label} className="sv-status-row">
                    <span className="sv-status-lbl">{s.label}</span>
                    <div className="sv-status-track">
                      <div className="sv-status-fill"
                        style={{ width: `${(s.count / Math.max(1, rows.length)) * 100}%` }} />
                    </div>
                    <span className="sv-status-num">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── TABLE ── */}
          <div className="sv-card sv-table-card">
            <div className="sv-table-head">
              <div>
                <div className="sv-card-title">Complaint list</div>
                <div className="sv-card-sub">
                  Click any row to open it in the Service Portal.
                  {ra.truncated && ' The portal caps this list at 200 rows and that cap was reached.'}
                </div>
              </div>
              <div className="sv-controls">
                <input className="sv-search" placeholder="Search number, customer, product, technician…"
                  value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                <select className="sv-select" value={zoneFilter}
                  onChange={e => { setZone(e.target.value); setPage(1); }}>
                  <option value="all">All zones</option>
                  {zoneOptions.map(z => <option key={z} value={z}>{cap(z)}</option>)}
                </select>
                <select className="sv-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="age">Oldest first</option>
                  <option value="newest">Newest first</option>
                  <option value="customer">By customer</option>
                </select>
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="sv-empty">No complaints match these filters</div>
            ) : (
              <>
                <div className="sv-table">
                  <div className="sv-thead">
                    <span>Complaint</span>
                    <span>Customer</span>
                    <span>Product &amp; Issue</span>
                    <span>Status</span>
                    <span>Technician</span>
                    <span>Age</span>
                  </div>
                  {pageRows.map(r => (
                    <a key={r.id || r.complaint_no}
                       className={`sv-trow ${r.is_urgent ? 'sv-urgent' : (r.unassigned || r.is_ageing) ? 'sv-warn' : ''}`}
                       href={r.admin_url} target="_blank" rel="noopener noreferrer">
                      <span className="sv-td-no">
                        #{r.complaint_no}
                        <span className="sv-td-zone" style={{ color: zoneColor(r.zone) }}>{cap(r.zone)}</span>
                      </span>
                      <span className="sv-td-cust">
                        <span className="sv-cust-name">{r.customer || '—'}</span>
                        <span className="sv-cust-loc">{r.location || '—'}</span>
                      </span>
                      <span className="sv-td-prod">
                        <span className="sv-prod-name">{r.product || '—'}</span>
                        <span className="sv-prod-issue">{r.issue || '—'}</span>
                      </span>
                      <span className="sv-td-status">
                        <span className="sv-status-badge">{r.status_label || '—'}</span>
                        <span className="sv-flags">
                          {r.is_urgent  && <span className="sv-flag sv-flag-red">urgent</span>}
                          {r.unassigned && <span className="sv-flag sv-flag-amber">unassigned</span>}
                          {r.is_ageing  && <span className="sv-flag sv-flag-amber">ageing</span>}
                          {r.at_service_centre && <span className="sv-flag sv-flag-blue">at centre</span>}
                        </span>
                      </span>
                      <span className="sv-td-tech">
                        {r.technician || <span className="sv-none">nobody</span>}
                      </span>
                      <span className="sv-td-age" style={{ color: ageColor(r.age_hours) }}>
                        {ageLabel(r.age_hours)}
                      </span>
                    </a>
                  ))}
                </div>

                <div className="sv-pager">
                  <button className="sv-page-btn" disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
                  <span className="sv-page-info">
                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, visible.length)} of {visible.length}
                    {visible.length !== rows.length && ` (filtered from ${rows.length})`}
                  </span>
                  <button className="sv-page-btn" disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next →</button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="sv-foot">
        Service portal data generated {data.generated_at ? fmtTime(data.generated_at) : '—'} ·
        {' '}Read-only bridge — this screen cannot change anything in the service portal
      </div>
    </div>
  );
}